// Phase 6b post-ship hotfix — frontend pre-save sanitization for per-row
// intensity. Spec: docs/superpowers/archive/specs/2026-05-18-spray-intensity-per-combo-design.md
//
// Why this helper exists:
//   The save flow's diffCombos may emit a patch `{ machineId, intensityLevel }`
//   for a row whose intensity label is not present on the new machine's latest
//   calibration. The backend's _validateIntensityOnMachine correctly rejects
//   that pair with 422. Rather than asking the user to manually clear
//   intensity before swapping machines, we sanitize the outbound payload
//   here so the editCombo / addCombo call lands successfully and the value
//   falls back via resolveComboIntensity on the next read.
//
// Returns:
//   true  — intensityLevel is null/undefined (no intensity is fine), OR the
//           machine's latest non-deleted calibration carries a level whose
//           `.label === intensityLevel`.
//   false — machine is present + intensityLevel is set + label not on the
//           machine's calibration (or machine has no calibrations).
//
// Mirrors the small `getLatestCalibration` heuristic in ComboListEditor.tsx;
// kept inline here to keep this helper self-contained and free of
// component-layer imports.

import type { SprayMachine, IntensityLabel } from '../services/sprayService';

export function isIntensityOnMachineCalibration(
  machine: SprayMachine | undefined,
  intensityLevel: IntensityLabel | null | undefined,
): boolean {
  // No intensity asserted → nothing to validate. The save flow drops the
  // key from the patch and the resolver falls back to the parent at read
  // time. This branch also fires when the user has not set a per-row
  // intensity yet.
  if (intensityLevel == null) return true;

  // Without a machine row we cannot validate; treat as invalid so the
  // caller drops the intensityLevel from the payload (the backend would
  // reject anyway since machineId is required on every combo).
  if (!machine?.calibrations || machine.calibrations.length === 0) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const active = machine.calibrations.filter((c: any) => c.status !== 'deleted');
  if (active.length === 0) return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const latest = [...active].sort((a: any, b: any) =>
    new Date(b.date).getTime() - new Date(a.date).getTime(),
  )[0];

  const levels = latest?.intensityLevels ?? [];
  return levels.some(l => l.label === intensityLevel);
}
